<script lang="ts">
	import HugeHeading from "@components/HugeHeading.svelte";
	import ProductCard from "@components/ProductCard.svelte";
	import ProductCatalogueFilter from "@components/ProductCatalogueFilter.svelte";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { partnerSearchParamLocalized } from "@utils/partnerSearchParamLocalized.js";
	import { on } from "svelte/events";
	import closeIcon from "@core/assets/icons/close.svg?raw";
	import filtersIcon from "@core/assets/icons/filters.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { ProductGroup } from "@core/schema/ProductGroup.js";
	import { categorySearchParamLocalized } from "@core/schema/paths/categorySearchParamLocalized.js";
	import { slugify } from "@core/utils/slugify.js";
	import type { ProductsCatalogueProps } from "./ProductsCatalogueProps.js";

	const { categories, partners, products, tenant }: ProductsCatalogueProps = $props();
	const productGroups = Object.values(ProductGroup);

	const categorySearchParam = categorySearchParamLocalized[tenant.locale];
	const partnerSearchParam = partnerSearchParamLocalized[tenant.locale];

	const filteredPartners = $derived(partners.filter(({ id }) => products.some(({ companyId }) => companyId === id)));
	const selectedCategoryIdsByProductGroups = $state(
		productGroups.reduce((previous, current) => ({ ...previous, [current]: [] }), {} as Record<ProductGroup, string[]>),
	);
	let selectedPartnerIds = $state<string[]>([]);
	const productsFilteredByPartner = $derived(
		selectedPartnerIds.length === 0
			? products
			: products.filter((product) => product.companyId !== undefined && selectedPartnerIds.includes(product.companyId)),
	);
	const filteredCategories = $derived.by(() => {
		const selected = Object.values(selectedCategoryIdsByProductGroups).flat();
		return categories.filter(
			({ id }) =>
				(selected.length === 0 || selected.includes(id)) &&
				productsFilteredByPartner.some(({ categoryIds }) => categoryIds.includes(id)),
		);
	});
	const noFiltersApplied = $derived(
		Object.values(selectedCategoryIdsByProductGroups).flat().length === 0 && selectedPartnerIds.length === 0,
	);

	function applyFilterToParams(params: URLSearchParams): void {
		params.delete(categorySearchParam);
		params.delete(partnerSearchParam);
		for (const categoryId of Object.values(selectedCategoryIdsByProductGroups).flat()) {
			const category = categories.find(({ id }) => id === categoryId);
			if (category) {
				params.append(categorySearchParam, `${slugify(category.name)}-${category.id.slice(0, 4)}`);
			}
		}
		for (const partnerId of selectedPartnerIds) {
			const partner = partners.find(({ id }) => id === partnerId);
			if (partner) {
				params.append(partnerSearchParam, `${slugify(partner.name)}-${partner.id.slice(0, 4)}`);
			}
		}
	}

	function applyParamsToFilter(): void {
		const params = new URLSearchParams(window.location.search);
		for (const group of productGroups) {
			selectedCategoryIdsByProductGroups[group] = [];
		}
		for (const slugifiedCategoryName of params.getAll(categorySearchParam)) {
			for (const { id, name, productGroup } of categories) {
				if (`${slugify(name)}-${id.slice(0, 4)}` === slugifiedCategoryName) {
					selectedCategoryIdsByProductGroups[productGroup].push(id);
				}
			}
		}
		const partnerIds = params.getAll(partnerSearchParam);
		if (partnerIds.length > 0) {
			selectedPartnerIds = partners
				.filter(({ name, id }) => partnerIds.includes(`${slugify(name)}-${id.slice(0, 4)}`))
				.map(({ id }) => id);
		} else {
			selectedPartnerIds = [];
		}
	}

	let changeUrlLively = $state(false);
	$effect(() => {
		void selectedCategoryIdsByProductGroups;
		void selectedPartnerIds;
		if (changeUrlLively) {
			const url = new URL(window.location.href);
			applyFilterToParams(url.searchParams);
			window.history.pushState("", "", url.toString());
		} else if (typeof window !== "undefined") {
			applyParamsToFilter();
			changeUrlLively = true;
		}
	});

	let dialog = $state<HTMLElement>();

	$effect(
		() =>
			dialog &&
			on(dialog, "click", ({ currentTarget, target }) => {
				if (currentTarget instanceof HTMLDialogElement && currentTarget === target) {
					currentTarget.close();
				}
			}),
	);

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet filters()}
	<div class="w-full flex items-center justify-between font-semibold mb-5 lg:mb-4">
		<div class="text-base lg:text-xl/7 font-bold text-black">
			<Formatted text="Filtry" />
		</div>
		<Button
			onclick={(): void => {
				for (const group of productGroups) {
					selectedCategoryIdsByProductGroups[group] = [];
				}
				selectedPartnerIds = [];
			}}
			variant={ButtonVariant.Text}
			class="text-cmcwarning !gap-1 disabled:opacity-50"
			disabled={noFiltersApplied}
		>
			<span class="uppercase text-xs tracking-[0.002em]">
				<Formatted text="Vymazat filtry" />
			</span>
			{#snippet icon()}
				<Icon icon={closeIcon} class="size-6" />
			{/snippet}
		</Button>
	</div>
	{#each productGroups as group (group)}
		<ProductCatalogueFilter bind:selected={selectedCategoryIdsByProductGroups[group]} {group} values={categories} />
	{/each}
	<ProductCatalogueFilter
		disabled={filteredCategories.length === 0}
		bind:selected={selectedPartnerIds}
		values={filteredPartners}
	/>
{/snippet}

<svelte:window onpopstate={applyParamsToFilter} />
<Layout>
	<div class="full-width text-center">
		<div class="lg:hidden flex items-center justify-center">
			<Button
				variant={ButtonVariant.Primary}
				onclick={(): void => {
					if (dialog instanceof HTMLDialogElement) {
						dialog.showModal();
					}
				}}
			>
				<Formatted text="Filtry" />
				{#snippet icon()}
					<Icon icon={filtersIcon} class="size-[2em]" />
				{/snippet}
			</Button>
		</div>
		<div
			class="flex gap-16 py-8 md:py-16 lg:py-20 max-w-[min(1664px,100%)] mx-auto text-left w-full min-w-0 !px-2 md:!px-8"
		>
			<div class="!w-[23rem] shrink-0 pt-28 hidden lg:flex flex-col gap-2">
				{@render filters()}
			</div>
			<main class="grow flex flex-col gap-12 md:gap-24">
				{#if filteredCategories.length === 0}
					<div class="pt-32 font-semibold">
						<Formatted
							text={noFiltersApplied
								? "Aktuálně nejsou žádné produkty dostupné."
								: "Vašemu vyhledávání neodpovídají žádné produkty"}
						/>
					</div>
				{/if}
				{#each filteredCategories as category (category)}
					<div class="flex flex-col w-full">
						<HugeHeading level={2} text={category.name} class="lg:!text-9xl text-center" />
						<div class="grid gap-1 lg:gap-2 max-w-screen-xl grid-cols-2 xl:grid-cols-3">
							{#each productsFilteredByPartner as product (product.id)}
								{#if product.categoryIds.includes(category.id)}
									<ProductCard {product} class="min-h-[19.125rem] md:min-h-[24.75rem]" />
								{/if}
							{/each}
						</div>
					</div>
				{/each}
			</main>
		</div>
	</div>
</Layout>

<dialog
	bind:this={dialog}
	class="top-0 m-0 w-full inset-x-0 h-fit p-4 max-w-full cmc-modal bg-white/75 backdrop-blur-lg"
>
	<div class="flex flex-col gap-1">
		{@render filters()}
	</div>
</dialog>
